import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Cpu,
  Palette,
  ShieldCheck,
  ShieldAlert,
  Eye,
  Ban,
  Search,
  Filter,
  RefreshCw,
  AlertTriangle,
  PenTool,
  CheckCheck,
  Terminal,
  Database,
  Lock,
  RotateCcw,
  SlidersHorizontal,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Badge } from '@/components/ui/badge.js';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage.js';
import { cn } from '@/lib/utils.js';

export interface McpToolItem {
  name: string;
  label: string;
  module: string;
  safety: 'read_only' | 'mutating' | 'destructive';
  description: string;
  data_touched: string;
  is_active: boolean;
  is_prompt?: boolean;
}

export function McpSettingsPage() {
  const { user } = useAuth();

  // Strict RBAC Guard: Owner role only
  if (user && user.role !== 'owner') {
    return (
      <UnauthorizedPage
        requiredRoles={['owner']}
        message="MCP Tool Configuration is strictly restricted to agency owners."
      />
    );
  }

  const [tools, setTools] = useState<McpToolItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingTool, setUpdatingTool] = useState<string | null>(null);
  const [applyingPreset, setApplyingPreset] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Filters state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedSafety, setSelectedSafety] = useState('all');

  const fetchTools = async () => {
    try {
      setLoading(true);
      const res = await api.get<any>('/api/v1/mcp/tools');
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.tools)
        ? res.tools
        : Array.isArray(res)
        ? res
        : [];
      setTools(list);
    } catch (err: any) {
      console.error('Failed to load MCP tools:', err);
      setFeedback({
        type: 'error',
        message: err?.message || 'Failed to fetch MCP tools inventory.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, []);

  // Handle single tool activation toggle
  const handleToggleTool = async (toolName: string, currentStatus: boolean) => {
    const nextStatus = !currentStatus;
    setUpdatingTool(toolName);

    // Optimistic UI update
    setTools((prev) =>
      prev.map((t) => (t.name === toolName ? { ...t, is_active: nextStatus } : t))
    );

    try {
      await api.patch(`/api/v1/mcp/tools/${encodeURIComponent(toolName)}`, {
        is_active: nextStatus,
      });
      setFeedback({
        type: 'success',
        message: `Tool '${toolName}' successfully ${nextStatus ? 'activated' : 'deactivated'}.`,
      });
      setTimeout(() => setFeedback(null), 3500);
    } catch (err: any) {
      console.error(`Failed to toggle tool ${toolName}:`, err);
      // Revert optimistic update
      setTools((prev) =>
        prev.map((t) => (t.name === toolName ? { ...t, is_active: currentStatus } : t))
      );
      setFeedback({
        type: 'error',
        message: err?.message || `Failed to update activation state for '${toolName}'.`,
      });
    } finally {
      setUpdatingTool(null);
    }
  };

  // Handle batch preset fast controls
  const handleApplyPreset = async (preset: 'enable_all' | 'disable_destructive' | 'read_only' | 'disable_all') => {
    setApplyingPreset(preset);
    try {
      const res = await api.post<any>('/api/v1/mcp/tools/batch', { preset });
      const updatedList = Array.isArray(res?.data?.tools)
        ? res.data.tools
        : Array.isArray(res?.tools)
        ? res.tools
        : null;

      if (updatedList) {
        setTools(updatedList);
      } else {
        await fetchTools();
      }

      const presetLabels: Record<string, string> = {
        enable_all: 'All tools enabled',
        disable_destructive: 'Destructive delete/conversion tools deactivated',
        read_only: 'Read-only mode enforced (all mutation & delete tools deactivated)',
        disable_all: 'Emergency kill-switch applied: All MCP tools disabled',
      };

      setFeedback({
        type: 'success',
        message: presetLabels[preset] || 'Batch preset applied successfully.',
      });
      setTimeout(() => setFeedback(null), 4000);
    } catch (err: any) {
      console.error(`Failed to apply preset ${preset}:`, err);
      setFeedback({
        type: 'error',
        message: err?.message || `Failed to apply preset: ${preset}.`,
      });
    } finally {
      setApplyingPreset(null);
    }
  };

  // Unique modules for filter dropdown
  const modulesList = useMemo(() => {
    const set = new Set<string>();
    tools.forEach((t) => set.add(t.module));
    return ['all', ...Array.from(set)];
  }, [tools]);

  // Statistics
  const stats = useMemo(() => {
    const total = tools.length;
    const active = tools.filter((t) => t.is_active).length;
    const deactivated = total - active;
    const readOnly = tools.filter((t) => t.safety === 'read_only').length;
    const mutating = tools.filter((t) => t.safety === 'mutating').length;
    const destructive = tools.filter((t) => t.safety === 'destructive').length;
    const percentActive = total > 0 ? Math.round((active / total) * 100) : 0;
    return { total, active, deactivated, readOnly, mutating, destructive, percentActive };
  }, [tools]);

  // Filtered tools list
  const filteredTools = useMemo(() => {
    return tools.filter((tool) => {
      // Search matching
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesName = tool.name.toLowerCase().includes(q);
        const matchesLabel = tool.label.toLowerCase().includes(q);
        const matchesDesc = tool.description.toLowerCase().includes(q);
        const matchesData = tool.data_touched.toLowerCase().includes(q);
        const matchesModule = tool.module.toLowerCase().includes(q);
        if (!matchesName && !matchesLabel && !matchesDesc && !matchesData && !matchesModule) {
          return false;
        }
      }

      // Module filter
      if (selectedModule !== 'all' && tool.module !== selectedModule) {
        return false;
      }

      // Status filter
      if (selectedStatus === 'active' && !tool.is_active) return false;
      if (selectedStatus === 'deactivated' && tool.is_active) return false;

      // Safety filter
      if (selectedSafety !== 'all' && tool.safety !== selectedSafety) return false;

      return true;
    });
  }, [tools, searchQuery, selectedModule, selectedStatus, selectedSafety]);

  const hasActiveFilters = searchQuery !== '' || selectedModule !== 'all' || selectedStatus !== 'all' || selectedSafety !== 'all';

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedModule('all');
    setSelectedStatus('all');
    setSelectedSafety('all');
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-brand-border pb-6">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#087F75] to-[#06685F] text-[#EFFFF9] flex items-center justify-center shadow-md">
              <Cpu className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-brand-ink">
                MCP Tool Configuration
              </h1>
              <p className="text-sm text-brand-muted">
                Control Model Context Protocol connectors, tool activation states, and security boundaries.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Badge
            variant="outline"
            className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/60"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>Strict Owner RBAC</span>
          </Badge>
          <Link to="/settings">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1.5 text-xs font-semibold"
            >
              <Palette className="h-3.5 w-3.5 text-brand-teal" />
              <span>Branding &amp; Settings</span>
            </Button>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchTools}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs font-semibold"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            <span>Refresh Roster</span>
          </Button>
        </div>
      </div>

      {/* Feedback Toast Banner */}
      {feedback && (
        <div
          className={cn(
            'flex items-center justify-between p-4 rounded-xl border text-sm font-medium transition-all shadow-sm animate-in fade-in slide-in-from-top-2',
            feedback.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800'
              : 'bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/50 dark:text-rose-300 dark:border-rose-800'
          )}
        >
          <div className="flex items-center gap-2.5">
            {feedback.type === 'success' ? (
              <CheckCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-rose-600 dark:text-rose-400 shrink-0" />
            )}
            <span>{feedback.message}</span>
          </div>
          <button
            onClick={() => setFeedback(null)}
            className="text-xs uppercase font-bold tracking-wider opacity-70 hover:opacity-100 px-2 py-1"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* KPI Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-brand-border bg-surface">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Total Tools</p>
              <h3 className="text-2xl font-black text-brand-ink mt-0.5">{stats.total}</h3>
              <p className="text-[11px] text-brand-muted mt-0.5">Full MCP Protocol Schema</p>
            </div>
            <div className="h-10 w-10 rounded-xl bg-brand-tint flex items-center justify-center text-brand">
              <Cpu className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-brand-border bg-surface">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Active Tools</p>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <h3 className="text-2xl font-black text-emerald-600 dark:text-emerald-400">{stats.active}</h3>
                <span className="text-xs text-brand-muted font-medium">({stats.percentActive}%)</span>
              </div>
              <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-0.5">Permitted for agents</p>
            </div>
            <div className="h-10 w-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-brand-border bg-surface">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Deactivated</p>
              <h3 className="text-2xl font-black text-rose-600 dark:text-rose-400 mt-0.5">{stats.deactivated}</h3>
              <p className="text-[11px] text-brand-muted mt-0.5">Blocked with 403 on invocation</p>
            </div>
            <div className="h-10 w-10 rounded-xl bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 flex items-center justify-center">
              <Ban className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-brand-border bg-surface">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Destructive Tools</p>
              <h3 className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-0.5">{stats.destructive}</h3>
              <p className="text-[11px] text-brand-muted mt-0.5">Delete & convert actions</p>
            </div>
            <div className="h-10 w-10 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Batch Presets & Fast Security Controls */}
      <Card className="border-brand-border bg-gradient-to-r from-surface to-brand-tint/30 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base font-bold flex items-center gap-2 text-brand-ink">
                <SlidersHorizontal className="h-4 w-4 text-brand" />
                <span>Fast Security Controls & Batch Presets</span>
              </CardTitle>
              <CardDescription className="text-xs text-brand-muted">
                Quickly adjust workspace MCP safety policies with one click.
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-[11px] font-medium border-brand-border">
              Applies instantly across API & MCP
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* 1. Enable All Tools */}
            <button
              onClick={() => handleApplyPreset('enable_all')}
              disabled={applyingPreset !== null}
              className="flex flex-col items-start p-3.5 rounded-xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/70 dark:bg-emerald-950/30 hover:bg-emerald-100/80 dark:hover:bg-emerald-900/40 transition-all text-left group disabled:opacity-50"
            >
              <div className="flex items-center gap-2 font-bold text-xs text-emerald-800 dark:text-emerald-300">
                <CheckCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform" />
                <span>Enable All Tools</span>
              </div>
              <p className="text-[11px] text-emerald-700/80 dark:text-emerald-400/80 mt-1 leading-snug">
                Activates full MCP capability spectrum across all 23+ workspace tools.
              </p>
            </button>

            {/* 2. Disable All Destructive Tools */}
            <button
              onClick={() => handleApplyPreset('disable_destructive')}
              disabled={applyingPreset !== null}
              className="flex flex-col items-start p-3.5 rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50/70 dark:bg-amber-950/30 hover:bg-amber-100/80 dark:hover:bg-amber-900/40 transition-all text-left group disabled:opacity-50"
            >
              <div className="flex items-center gap-2 font-bold text-xs text-amber-800 dark:text-amber-300">
                <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform" />
                <span>Disable Destructive Tools</span>
              </div>
              <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 mt-1 leading-snug">
                Deactivates all <code className="font-mono text-[10px] bg-amber-100 dark:bg-amber-900/60 px-1 rounded">delete_*</code> and conversion tools to prevent data loss.
              </p>
            </button>

            {/* 3. Read-Only Mode */}
            <button
              onClick={() => handleApplyPreset('read_only')}
              disabled={applyingPreset !== null}
              className="flex flex-col items-start p-3.5 rounded-xl border border-sky-200 dark:border-sky-900/60 bg-sky-50/70 dark:bg-sky-950/30 hover:bg-sky-100/80 dark:hover:bg-sky-900/40 transition-all text-left group disabled:opacity-50"
            >
              <div className="flex items-center gap-2 font-bold text-xs text-sky-800 dark:text-sky-300">
                <Eye className="h-4 w-4 text-sky-600 dark:text-sky-400 group-hover:scale-110 transition-transform" />
                <span>Read-Only Mode</span>
              </div>
              <p className="text-[11px] text-sky-700/80 dark:text-sky-400/80 mt-1 leading-snug">
                Enables only <code className="font-mono text-[10px] bg-sky-100 dark:bg-sky-900/60 px-1 rounded">list_*</code> and <code className="font-mono text-[10px] bg-sky-100 dark:bg-sky-900/60 px-1 rounded">get_*</code> inspection tools.
              </p>
            </button>

            {/* 4. Disable All Tools (Kill Switch) */}
            <button
              onClick={() => handleApplyPreset('disable_all')}
              disabled={applyingPreset !== null}
              className="flex flex-col items-start p-3.5 rounded-xl border border-rose-200 dark:border-rose-900/60 bg-rose-50/70 dark:bg-rose-950/30 hover:bg-rose-100/80 dark:hover:bg-rose-900/40 transition-all text-left group disabled:opacity-50"
            >
              <div className="flex items-center gap-2 font-bold text-xs text-rose-800 dark:text-rose-300">
                <Ban className="h-4 w-4 text-rose-600 dark:text-rose-400 group-hover:scale-110 transition-transform" />
                <span>Disable All Tools (Kill-Switch)</span>
              </div>
              <p className="text-[11px] text-rose-700/80 dark:text-rose-400/80 mt-1 leading-snug">
                Emergency lockdown blocking external agents from invoking any MCP tool.
              </p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Filter and Search Bar Toolbar */}
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 p-3.5 bg-surface rounded-xl border border-brand-border">
        {/* Real-time Search */}
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
          <Input
            placeholder="Search tools by name, description, or data touched..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-brand-surface/40 h-9 text-xs"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-brand-muted hover:text-brand-ink"
            >
              ✕
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Module Filter */}
          <div className="flex items-center gap-1 text-xs">
            <span className="text-brand-muted font-medium hidden sm:inline">Module:</span>
            <select
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              className="h-9 rounded-lg border border-brand-border bg-surface px-2.5 text-xs font-medium text-brand-ink focus:outline-none focus:ring-1 focus:ring-brand"
            >
              <option value="all">All Modules</option>
              {modulesList.filter((m) => m !== 'all').map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-1 text-xs">
            <span className="text-brand-muted font-medium hidden sm:inline">Status:</span>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="h-9 rounded-lg border border-brand-border bg-surface px-2.5 text-xs font-medium text-brand-ink focus:outline-none focus:ring-1 focus:ring-brand"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="deactivated">Deactivated Only</option>
            </select>
          </div>

          {/* Safety Filter */}
          <div className="flex items-center gap-1 text-xs">
            <span className="text-brand-muted font-medium hidden sm:inline">Safety:</span>
            <select
              value={selectedSafety}
              onChange={(e) => setSelectedSafety(e.target.value)}
              className="h-9 rounded-lg border border-brand-border bg-surface px-2.5 text-xs font-medium text-brand-ink focus:outline-none focus:ring-1 focus:ring-brand"
            >
              <option value="all">All Safety Levels</option>
              <option value="read_only">Read-Only</option>
              <option value="mutating">Mutating / Write</option>
              <option value="destructive">Destructive / Delete</option>
            </select>
          </div>

          {/* Reset Filters Button */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="h-9 px-2.5 text-xs text-brand-muted hover:text-brand-ink flex items-center gap-1"
            >
              <RotateCcw className="h-3 w-3" />
              <span>Reset</span>
            </Button>
          )}
        </div>
      </div>

      {/* Tools Roster Grid */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-sm font-bold text-brand-ink uppercase tracking-wider flex items-center gap-2">
            <Database className="h-4 w-4 text-brand" />
            <span>Tools Inventory ({filteredTools.length} of {tools.length})</span>
          </h2>
          <span className="text-xs text-brand-muted">
            Click toggle switch to activate or deactivate any tool
          </span>
        </div>

        {filteredTools.length === 0 ? (
          <Card className="border-brand-border bg-surface p-12 text-center">
            <div className="h-12 w-12 rounded-full bg-brand-tint mx-auto flex items-center justify-center text-brand mb-3">
              <Filter className="h-6 w-6" />
            </div>
            <h3 className="text-base font-bold text-brand-ink">No MCP tools match your filters</h3>
            <p className="text-xs text-brand-muted mt-1 max-w-sm mx-auto">
              Try adjusting your search query, module selection, or safety classification filter.
            </p>
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={resetFilters}
                className="mt-4 text-xs font-semibold"
              >
                Clear All Filters
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {filteredTools.map((tool) => {
              const isBusy = updatingTool === tool.name;

              return (
                <div
                  key={tool.name}
                  className={cn(
                    'relative rounded-xl border p-4 transition-all duration-normal flex flex-col justify-between gap-3',
                    tool.is_active
                      ? 'bg-surface border-brand-border hover:border-brand/40 shadow-xs'
                      : 'bg-brand-surface/40 border-brand-border/60 opacity-80'
                  )}
                >
                  {/* Top Row: Name, Label, Badges & Interactive Switch */}
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm text-brand-ink">{tool.label}</span>
                          <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-brand-surface text-brand-ink/80 border border-brand-border">
                            {tool.name}
                          </span>
                        </div>

                        {/* Badges */}
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          {/* Module Badge */}
                          <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-brand-tint text-brand">
                            {tool.module}
                          </span>

                          {/* Safety Classification Badge */}
                          {tool.safety === 'read_only' && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/60 flex items-center gap-1"
                            >
                              <Eye className="h-2.5 w-2.5" />
                              <span>Read-Only</span>
                            </Badge>
                          )}
                          {tool.safety === 'mutating' && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-2 py-0.5 bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-400 dark:border-sky-800/60 flex items-center gap-1"
                            >
                              <PenTool className="h-2.5 w-2.5" />
                              <span>Mutating / Write</span>
                            </Badge>
                          )}
                          {tool.safety === 'destructive' && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-2 py-0.5 bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800/60 flex items-center gap-1"
                            >
                              <AlertTriangle className="h-2.5 w-2.5" />
                              <span>Destructive / Delete</span>
                            </Badge>
                          )}

                          {/* MCP Prompt Badge */}
                          {tool.is_prompt && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-2 py-0.5 bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-400 dark:border-purple-800/60 flex items-center gap-1"
                            >
                              <Terminal className="h-2.5 w-2.5" />
                              <span>MCP Prompt</span>
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Interactive Toggle Switch */}
                      <div className="flex flex-col items-end shrink-0 pl-2">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={tool.is_active}
                          disabled={isBusy}
                          onClick={() => handleToggleTool(tool.name, tool.is_active)}
                          className={cn(
                            'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#087F75] focus:ring-offset-2 disabled:opacity-50',
                            tool.is_active
                              ? 'bg-[#087F75]'
                              : 'bg-zinc-300 dark:bg-zinc-700'
                          )}
                          title={tool.is_active ? `Click to deactivate ${tool.name}` : `Click to activate ${tool.name}`}
                        >
                          <span
                            className={cn(
                              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out',
                              tool.is_active ? 'translate-x-5' : 'translate-x-0'
                            )}
                          />
                        </button>
                        <span
                          className={cn(
                            'text-[10px] font-bold uppercase tracking-wider mt-1 select-none',
                            tool.is_active
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-zinc-500 dark:text-zinc-400'
                          )}
                        >
                          {tool.is_active ? 'Active' : 'Deactivated'}
                        </span>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-xs text-brand-ink/90 mt-2.5 leading-relaxed">
                      {tool.description}
                    </p>
                  </div>

                  {/* Bottom Metadata: Data Touched */}
                  <div className="pt-2 border-t border-brand-border/60 flex items-center justify-between text-[11px] text-brand-muted">
                    <span className="flex items-center gap-1 truncate max-w-[80%]">
                      <Lock className="h-3 w-3 shrink-0 text-brand-muted" />
                      <span className="truncate">Touched: <span className="font-medium text-brand-ink/80">{tool.data_touched}</span></span>
                    </span>
                    <span className="text-[10px] font-mono">
                      {tool.is_active ? 'HTTP 200' : 'HTTP 403'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default McpSettingsPage;
